import React, { useState, useEffect } from 'react';
import { Container, Row, Col } from 'react-bootstrap';
import { 
  Heart,
  Mail,
  Phone,
  MapPin,
  Facebook,
  Twitter,
  Instagram,
  Linkedin,
  Youtube,
  ArrowUp,
  ExternalLink
} from 'lucide-react';
import '../../../assets/css/home/Footer.css';

const Footer = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [showBackToTop, setShowBackToTop] = useState(false);

  useEffect(() => {
    // Scroll animation observer
    const observeElements = () => {
      const elements = document.querySelectorAll('.sdms-footer-animate');
      const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.classList.add('sdms-visible');
          }
        });
      }, { threshold: 0.1 });

      elements.forEach(el => observer.observe(el));
    };
    observeElements();

    // Back to top button visibility
    const handleScroll = () => {
      if (window.scrollY > 300) {
        setShowBackToTop(true);
      } else {
        setShowBackToTop(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Scroll to top function
  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  // Quick Links
  const quickLinks = [
    { name: 'Home', path: '/' },
    { name: 'About Us', path: '/about' },
    { name: 'How It Works', path: '/how-it-works' },
    { name: 'Donations', path: '/listings' },
    { name: 'Featured Donors', path: '/donors' },
    { name: 'Contact Us', path: '/contact' }
  ];

  // Support Links
  const supportLinks = [
    { name: 'Help Center', path: '/help' },
    { name: 'FAQs', path: '/faqs' },
    { name: 'Privacy Policy', path: '/privacy' },
    { name: 'Terms & Conditions', path: '/terms' },
    { name: 'Cookie Policy', path: '/cookies' }
  ];

  // Social Media Links
  const socialLinks = [
    { 
      name: 'Facebook', 
      icon: Facebook, 
      url: 'https://facebook.com/sdms',
      color: '#1877F2'
    },
    { 
      name: 'Twitter', 
      icon: Twitter, 
      url: 'https://twitter.com/sdms',
      color: '#1DA1F2'
    },
    { 
      name: 'Instagram', 
      icon: Instagram, 
      url: 'https://instagram.com/sdms',
      color: '#E4405F'
    },
    { 
      name: 'LinkedIn', 
      icon: Linkedin, 
      url: 'https://linkedin.com/company/sdms',
      color: '#0A66C2'
    },
    { 
      name: 'YouTube', 
      icon: Youtube, 
      url: 'https://youtube.com/@sdms',
      color: '#FF0000'
    }
  ];

  return (
    <footer className="sdms-footer-section">
      {/* Main Footer */}
      <div className="sdms-footer-main">
        <Container>
          <Row className="g-4">
            {/* About Column */}
            <Col lg={4} md={6}>
              <div className="sdms-footer-column sdms-footer-animate">
                <div className="sdms-footer-logo">
                  <Heart size={32} fill="#FFB800" stroke="#FFB800" />
                  <h3>SDMS</h3>
                </div>
                <p className="sdms-footer-about">
                  Smart Donation Management System connects donors, receivers, and NGOs 
                  to make a meaningful impact. Together, we're building a better tomorrow 
                  through transparent and efficient donation management.
                </p>
                
                {/* Contact Info */}
                <div className="sdms-footer-contact">
                  <div className="sdms-contact-item">
                    <Phone size={18} />
                    <a href="tel:+923001234567">+92 300 1234567</a>
                  </div>
                  <div className="sdms-contact-item">
                    <Mail size={18} />
                    <a href="mailto:info@sdms.org">info@sdms.org</a>
                  </div>
                  <div className="sdms-contact-item">
                    <MapPin size={18} />
                    <span>Karachi, Pakistan</span>
                  </div>
                </div>
              </div>
            </Col>

            {/* Quick Links Column */}
            <Col lg={2} md={6}>
              <div className="sdms-footer-column sdms-footer-animate" style={{ animationDelay: '0.1s' }}>
                <h4 className="sdms-footer-heading">Quick Links</h4>
                <ul className="sdms-footer-links">
                  {quickLinks.map((link, index) => (
                    <li key={index}>
                      <a href={link.path} className="sdms-footer-link">
                        {link.name}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            </Col>

            {/* Support Column */}
            <Col lg={3} md={6}>
              <div className="sdms-footer-column sdms-footer-animate" style={{ animationDelay: '0.2s' }}>
                <h4 className="sdms-footer-heading">Support</h4>
                <ul className="sdms-footer-links">
                  {supportLinks.map((link, index) => (
                    <li key={index}>
                      <a href={link.path} className="sdms-footer-link">
                        {link.name}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            </Col>

            {/* Newsletter Column */}
            <Col lg={3} md={6}>
              <div className="sdms-footer-column sdms-footer-animate" style={{ animationDelay: '0.3s' }}>
                <h4 className="sdms-footer-heading">Stay Connected</h4>
                <p className="sdms-newsletter-text">
                  Subscribe to get updates on new donations and success stories.
                </p>
                
                {/* Newsletter Form */}
                <div className="sdms-newsletter-form">
                  <input 
                    type="email" 
                    placeholder="Enter your email" 
                    className="sdms-newsletter-input"
                  />
                  <button className="sdms-newsletter-button">
                    Subscribe
                  </button>
                </div>

                {/* Social Media Links */}
                <div className="sdms-social-links">
                  {socialLinks.map((social, index) => {
                    const Icon = social.icon;
                    return (
                      <a
                        key={index}
                        href={social.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="sdms-social-link"
                        style={{ '--social-color': social.color }}
                        title={social.name}
                      >
                        <Icon size={20} />
                      </a>
                    );
                  })}
                </div>
              </div>
            </Col>
          </Row>
        </Container>
      </div>

      {/* Footer Bottom */}
      <div className="sdms-footer-bottom">
        <Container>
          <Row className="align-items-center">
            <Col md={6} className="text-center text-md-start">
              <p className="sdms-footer-copyright">
                © {new Date().getFullYear()} SDMS. All rights reserved.
              </p>
            </Col>
            <Col md={6} className="text-center text-md-end">
              <div className="sdms-footer-bottom-links">
                <a href="/privacy" className="sdms-footer-bottom-link">Privacy</a>
                <span className="sdms-separator">•</span>
                <a href="/terms" className="sdms-footer-bottom-link">Terms</a>
                <span className="sdms-separator">•</span>
                <a href="/cookies" className="sdms-footer-bottom-link">Cookies</a>
              </div>
            </Col>
          </Row>
        </Container>
      </div>

      {/* Back to Top Button */}
      <button 
        className={`sdms-back-to-top ${showBackToTop ? 'sdms-visible' : ''}`}
        onClick={scrollToTop}
        aria-label="Back to top"
      >
        <ArrowUp size={24} />
      </button>
    </footer>
  );
};

export default Footer;